import { captureException } from '@dapperlabs/core-fe';
import fromEntries from 'object.fromentries';

import {
  TEAM_DATA,
  TEAM_LOGO_BUCKET_URL,
  CURRENT_SEASON,
  Branding,
  BrandingUpdate,
  ShortNames,
} from './teamData';

/**
 * @private
 * @param {string} season - Liga season, eg. '2020-21'
 * @returns {number} year - second year of a Liga season eg. 2021
 */
function seasonToYear(season: string): number {
  const nbaSeasonPattern = /\d{4}-\d{2}/g; // Matches 1999-20, 2020-21, etc
  if (season?.match(nbaSeasonPattern)) {
    return Number(season?.split('-')[0]) + 1;
  }
  return Number(season);
}

/**
 * @private
 * @param {string} shortName - eg. 'Deportivo Alavés'
 * @param {string} year - year of the liga season, the result of `seasonToYear()`
 */
function applyUpdateForTeamAndYear(shortName: ShortNames, year: number) {
  return function applyTeamUpdate(team: Branding, update: BrandingUpdate) {
    if (update.shortName !== shortName) return team; // Update is for a different team
    if (update.year > year) return team; // Update is after the desired year
    if (update.year < team.year) return team; // Update is older than info we already have
    // merge current info with update
    return { ...team, ...update };
  };
}

/**
 * @private
 * @param {object} Branding
 * @returns {object} team - logoPath decorated
 */
function decorateTeamLogo(team: Branding): Branding {
  team.logoPath = `${TEAM_LOGO_BUCKET_URL}${team.logoPath}/${team.slug}.svg`;
  return team;
}

/**
 * @private
 * @param {object} Branding
 * @returns {object} team - decorated
 * note additional decorators can be chained:
 * eg. decorateFutureTeamProp(decorateTeamAltLogo(decorateTeamLogo(team)))
 */
function decorateTeam(team: Branding): Branding {
  return decorateTeamLogo(team);
}

// this helps make lookups quicker 🐰
const MEMO = {};

/**
 * @private
 * @param {string} shortName - eg. 'Deportivo Alavés'
 * @param {integer} year - eg 1999 (not a season)
 * @returns {string}
 */
function memoKey(shortName: string, year: number): string {
  return [shortName, year].join('|');
}

// This pulls the team IDs from the TEAM_DATA object
// Note, we're currently using ShortNames as IDs
const TEAM_IDS = Object.keys(TEAM_DATA);

// End of private helpers

/**
 * getTeamData - the main function in this utility, most other utilities rely on
 * it. Please be careful ⚠️
 * @param {string} shortName - eg. 'Deportivo Alavés'
 * @param {string} season - LaLiga season, eg. 2022
 * @returns {object} team branding data
 */
export function getTeamData(
  shortName: ShortNames,
  season: string = CURRENT_SEASON,
): Branding {
  if (!shortName) return null;
  const year = seasonToYear(season);
  const key = memoKey(shortName, year);
  if (MEMO[key] != null) return MEMO[key];
  if (!TEAM_DATA[shortName]) {
    captureException(new Error(`branding not found for ${shortName}`));
    return null;
  }
  MEMO[key] = decorateTeam(
    TEAM_DATA[shortName].reduce<Branding>(
      applyUpdateForTeamAndYear(shortName, year),
      TEAM_DATA[shortName][0],
    ),
  );
  return MEMO[key];
}

/**
 * getTeamBrandingSeasons - returns an array of seasons where the branding was
 * updated.
 * @param {string} shortName - eg. 'Deportivo Alavés'
 * @returns [season]
 */
export function getTeamBrandingSeasons(shortName: ShortNames): number[] {
  if (!shortName) return null;

  return TEAM_DATA[shortName].map(({ year }) => year);
}

/**
 * getAllTeamsData - similar to getTeamData, but it returns all the teams
 * @param {string} season - LaLiga season, eg. 2022
 * @returns {object} - all teams branding data, keyed by teamId
 */
export function getAllTeamsData(season: string = CURRENT_SEASON): Branding[] {
  const year = seasonToYear(season);
  const key = year;
  if (MEMO[key]) return MEMO[key];
  MEMO[key] = fromEntries(
    TEAM_IDS.map((shortName: ShortNames) => [
      shortName,
      getTeamData(shortName, season),
    ]),
  );
  return MEMO[key];
}

/**
 * getTeamShortNames - ShortNames are used to fetch other details so this is important
 * @returns [array] - team names
 */
export const getTeamShortNames = (): ShortNames[] =>
  Object.values(getAllTeamsData())
    .map((team) => team.shortName)
    .sort();

/**
 * getTeamNames
 * @returns [array] - team names
 */
export const getTeamNames = (): string[] =>
  Object.values(getAllTeamsData())
    .map((team) => team.name)
    .sort();

// Utilities to get specific team branding data

export function getTeamLogo(
  shortName: ShortNames,
  season: string = CURRENT_SEASON,
): string {
  return getTeamData(shortName, season)?.logoPath;
}

export function getTeamAbbreviation(
  shortName: ShortNames,
  season: string = CURRENT_SEASON,
): string {
  return getTeamData(shortName, season)?.abbreviation;
}
